import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getPortalDashboard, setupStripeConnect } from '@/services/affiliateApi';

export default function AffiliatePortalDashboard() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: getPortalDashboard,
  });

  const handleCopyCode = () => {
    if (data?.promo_code?.code) {
      navigator.clipboard.writeText(data.promo_code.code);
      toast.success('Referral code copied!');
    }
  };

  const handleSetupStripeConnect = async () => {
    try {
      const result = await setupStripeConnect({
        return_url: window.location.origin + '/portal',
        refresh_url: window.location.origin + '/portal',
      });
      if (result.url) {
        window.open(result.url, '_blank');
      } else if (result.already_setup) {
        toast.success('Stripe Connect is already set up!');
      }
    } catch (err) {
      toast.error('Failed to setup Stripe Connect');
    }
  };

  if (isLoading) return <div className="text-gray-500 p-8">Loading dashboard...</div>;
  if (!data) return <div className="text-red-500 p-8">Failed to load dashboard</div>;

  const { partner, promo_code, recent_referrals } = data as any;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Affiliate Dashboard</h1>

      {/* Stripe Connect Banner */}
      {!partner.stripe_onboarding_complete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-yellow-800">Set up Stripe Connect to receive payouts</p>
            <p className="text-xs text-yellow-600 mt-1">
              You need to connect your Stripe account before you can receive commission payouts.
            </p>
          </div>
          <Button onClick={handleSetupStripeConnect}>Setup Stripe Connect</Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Referrals</p>
            <p className="text-2xl font-bold text-blue-600">{partner.total_referrals}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Qualified Referrals</p>
            <p className="text-2xl font-bold text-green-600">{partner.total_qualified_referrals}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Earned</p>
            <p className="text-2xl font-bold text-green-600">
              ${(partner.total_commission_earned_cents / 100).toFixed(2)}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Pending Payout</p>
            <p className="text-2xl font-bold text-orange-600">
              ${(partner.pending_payout_cents / 100).toFixed(2)}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Referral Code */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Your Referral Code</h2>
        </CardHeader>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-6 py-3">
              <span className="text-2xl font-mono font-bold text-blue-700">
                {promo_code?.code || 'N/A'}
              </span>
            </div>
            <Button onClick={handleCopyCode} variant="secondary">Copy Code</Button>
            <div className="text-sm text-gray-500">
              <p>Share this code with businesses you refer to Linked By Six.</p>
              <p>They get <strong>10% off</strong> their first month. You earn <strong>10% commission</strong> for up to 3 months.</p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Share link: <code className="bg-gray-100 px-2 py-1 rounded text-xs">
              https://linkedbysix.com/join?ref={promo_code?.code}
            </code>
          </div>
        </CardBody>
      </Card>

      {/* Recent Referrals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Referrals</h2>
            <Button size="sm" variant="secondary" onClick={() => navigate('/portal/referrals')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {!recent_referrals?.length ? (
            <p className="text-gray-500 text-center py-4">No referrals yet. Start sharing your code!</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recent_referrals.map((r: any) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-sm">{r.referred_business?.business_name || 'Pending'}</td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant={
                        r.status === 'qualified' ? 'success' :
                        r.status === 'churned' ? 'danger' :
                        r.status === 'commission_complete' ? 'secondary' : 'warning'
                      }>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm">{r.billing_cycle || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
